import {makeAutoObservable, runInAction} from 'mobx';
import type {CreateOrderUseCase} from '@/core/application/use-cases/order/CreateOrderUseCase';
import type {GetOrdersUseCase} from '@/core/application/use-cases/order/GetOrdersUseCase';
import type {CreateCheckoutSessionUseCase} from '@/core/application/use-cases/checkout/CreateCheckoutSessionUseCase';
import type {
    InitializePaymentParams,
    InitializePaymentUseCase
} from '@/core/application/use-cases/payment/InitializePaymentUseCase';
import type {ApiPaymentService, UpcomingPayment} from '@/core/infrastructure/services/ApiPaymentService';
import {CheckoutSessionResult, CheckoutSessionService} from '@/core/infrastructure/services/ApiPaymentService';
import {OrderStatus, type Order, type PaymentType} from '@/core/domain/entities/Order';
import type { CreateOrderRequest, OrderFiltersRequest } from '@/core/application/dtos/OrderDto';

export interface CheckoutSessionData {
    productId: string;
    quantity: number;
    paymentMethod: string;
    paymentPlan?: {
        durationMonths: number;
        frequency: string;
        kmpEnabled: boolean;
    };
    deliveryOption?: string;
    deliveryAddressId?: string;
}

export class OrderStore {
    // State
    orders: Order[] = [];
    currentOrder: Order | null = null;
    userOrders: Order[] = [];
    pendingOrders: Order[] = [];
    ordersRequiringPayment: Order[] = [];
    overdueOrders: Order[] = [];
    upcomingPayments: UpcomingPayment[] = [];

    // Checkout session state
    currentCheckoutSession: CheckoutSessionResult | null = null;
    isCreatingCheckoutSession = false;
    isInitializingPayment = false;
    paymentInitialization: any = null;

    isLoading = false;
    isCreating = false;
    error: string | null = null;
    currentFilters: OrderFiltersRequest = {};

    constructor(
        private readonly createOrderUseCase: CreateOrderUseCase,
        private readonly getOrdersUseCase: GetOrdersUseCase,
        private readonly createCheckoutSessionUseCase: CreateCheckoutSessionUseCase,
        private readonly initializePaymentUseCase: InitializePaymentUseCase,
        private readonly checkoutSessionService: CheckoutSessionService,
        private readonly paymentService: ApiPaymentService
    ) {
        makeAutoObservable(this);
    }

    // Computed values
    get hasOrders(): boolean {
        return this.orders.length > 0;
    }

    get hasUserOrders(): boolean {
        return this.userOrders.length > 0;
    }

    get hasPendingOrders(): boolean {
        return this.pendingOrders.length > 0;
    }

    get hasOrdersRequiringPayment(): boolean {
        return this.ordersRequiringPayment.length > 0;
    }

    get hasOverdueOrders(): boolean {
        return this.overdueOrders.length > 0;
    }

    get isOrderLoaded(): boolean {
        return this.currentOrder !== null;
    }

    get totalOrderValue(): number {
        return this.userOrders.reduce((total, order) => total + Number(order.product.price), 0);
    }

    get totalOutstandingBalance(): number {
        return this.userOrders.reduce((total, order) => total + Number(order.pss_plan.payments_remaining), 0);
    }

    // Checkout session computed values
    get hasActiveCheckoutSession(): boolean {
        return this.currentCheckoutSession !== null;
    }

    get checkoutSessionExpired(): boolean {
        if (!this.currentCheckoutSession) return false;
        return new Date(this.currentCheckoutSession.expiresAt) < new Date();
    }

    get checkoutSessionStatus(): string {
        return this.currentCheckoutSession?.status || 'none';
    }

    get totalCheckoutAmount(): string {
        return this.currentCheckoutSession?.paymentBreakdown.totalAmount || '0';
    }

    get amountDueNow(): string {
        return this.currentCheckoutSession?.paymentBreakdown.amountDueNow ||
            this.currentCheckoutSession?.paymentBreakdown.totalAmount || '0';
    }

    get remainingBalance(): string {
        return this.currentCheckoutSession?.paymentBreakdown.remainingBalance || '0';
    }

    get monthlyContribution(): string {
        return this.currentCheckoutSession?.paymentBreakdown.monthlyContribution || '0';
    }

    get hasUpcomingPayments(): boolean {
        return this.upcomingPayments.length > 0;
    }

    get upcomingPaymentsThisWeek(): any[] {
        const oneWeekFromNow = new Date();
        oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);

        return this.upcomingPayments.filter(payment => {
            const dueDate = new Date(payment.due_date);
            return dueDate <= oneWeekFromNow;
        });
    }

    get overduePayments(): any[] {
        const today = new Date();
        return this.upcomingPayments.filter(payment => {
            const dueDate = new Date(payment.due_date);
            return dueDate < today && payment.status === 'scheduled';
        });
    }

    // Actions
    async createOrder(request: CreateOrderRequest, userId: string): Promise<void> {
        this.setCreating(true);
        this.setError(null);

        try {
            const orderId = `order_${Date.now()}`;

            const result = await this.createOrderUseCase.execute({
                orderId,
                userId,
                ...request
            });

            runInAction(() => {
                this.currentOrder = result.order;
                // Add to user orders if they exist
                if (this.userOrders.length > 0) {
                    this.userOrders.unshift(this.currentOrder);
                }
            });

        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to create order');
            throw error;
        } finally {
            this.setCreating(false);
        }
    }

    async loadOrderById(id: string): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const order = await this.getOrdersUseCase.getById(id);

            runInAction(() => {
                this.currentOrder = order;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load order');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    async loadOrderByOrderNo(orderNo: number): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const order = await this.getOrdersUseCase.getByOrderNo(orderNo);

            runInAction(() => {
                this.currentOrder = order
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load order');
            throw error;
        } finally {
            this.setLoading(false);
        }
    }

    async loadUserOrders(userId: string, filters?: OrderFiltersRequest): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const orders = await this.getOrdersUseCase.getByUserId(userId, filters as any);

            runInAction(() => {
                this.userOrders = orders;
                this.currentFilters = filters || {};
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load user orders');
        } finally {
            this.setLoading(false);
        }
    }

    async loadAllOrders(filters?: OrderFiltersRequest): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const orders = await this.getOrdersUseCase.getAll(filters as any);

            runInAction(() => {
                this.orders = orders;
                this.currentFilters = filters || {};
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load orders');
        } finally {
            this.setLoading(false);
        }
    }

    async loadUserOrderHistory(userId: string, limit: number = 10): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const orders = await this.getOrdersUseCase.getUserOrderHistory(userId, limit);

            runInAction(() => {
                this.userOrders = orders;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load order history');
        } finally {
            this.setLoading(false);
        }
    }

    async loadPendingOrders(userId?: string): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const orders = await this.getOrdersUseCase.getPendingOrders(userId);

            runInAction(() => {
                this.pendingOrders = orders;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load pending orders');
        } finally {
            this.setLoading(false);
        }
    }

    async loadOrdersRequiringPayment(userId: string): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const orders = await this.getOrdersUseCase.getOrdersRequiringPayment(userId);

            runInAction(() => {
                this.ordersRequiringPayment = orders;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load orders requiring payment');
        } finally {
            this.setLoading(false);
        }
    }

    async loadOverdueOrders(userId?: string): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const orders = await this.getOrdersUseCase.getOverdueOrders(userId);

            runInAction(() => {
                this.overdueOrders = orders;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load overdue orders');
        } finally {
            this.setLoading(false);
        }
    }

    // Checkout session actions
    async createCheckoutSession(data: CheckoutSessionData, userId: string): Promise<void> {
        this.isCreatingCheckoutSession = true;
        this.setError(null);

        try {
            const result = await this.createCheckoutSessionUseCase.execute({
                productId: data.productId,
                quantity: data.quantity,
                userId,
                paymentMethod: data.paymentMethod,
                paymentPlan: data.paymentPlan,
                deliveryOption: data.deliveryOption,
                deliveryAddressId: data.deliveryAddressId
            });

            runInAction(() => {
                this.currentCheckoutSession = result;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to create checkout session');
            throw error;
        } finally {
            this.isCreatingCheckoutSession = false;
        }
    }

    async initializePayment(intent: InitializePaymentParams['intent'], phoneNumber?: string, _amount?: string): Promise<void> {
        if (!this.currentCheckoutSession) {
            throw new Error('No active checkout session');
        }

        this.isInitializingPayment = true;
        this.setError(null);

        try {
            const result = await this.initializePaymentUseCase.execute({
                paymentMethod: "", paymentProvider: "", userId: "",
                intent,
                context: {
                    checkoutSessionId: this.currentCheckoutSession.sessionId,
                    orderId: this.currentCheckoutSession.orderId,
                },
                // provider: 'mtn_momo',
                // method: 'mobile_money',
                phoneNumber: phoneNumber || '',
                // amount: amount || this.currentCheckoutSession.paymentBreakdown.amountDueNow,
                idempotencyKey: `payment_${Date.now()}`
            });

            runInAction(() => {
                this.paymentInitialization = result;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to initialize payment');
            throw error;
        } finally {
            this.isInitializingPayment = false;
        }
    }

    async loadUpcomingPayments(userId: string): Promise<void> {
        this.setLoading(true);
        this.setError(null);

        try {
            const payments = await this.paymentService.getUpcomingPayments(userId);

            runInAction(() => {
                this.upcomingPayments = payments.results;
            });
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to load upcoming payments');
        } finally {
            this.setLoading(false);
        }
    }

    clearCheckoutSession(): void {
        this.currentCheckoutSession = null;
        this.paymentInitialization = null;
    }

    // Helper methods
    setCurrentOrder(order: Order | null): void {
        this.currentOrder = order;
    }

    clearOrders(): void {
        this.orders = [];
        this.userOrders = [];
        this.pendingOrders = [];
        this.ordersRequiringPayment = [];
        this.overdueOrders = [];
        this.currentOrder = null;
    }

    getOrdersByStatus(status: OrderStatus): Order[] {
        return this.userOrders.filter(order => order.payment_status === status);
    }

    getOrdersByPaymentType(paymentType: PaymentType): Order[] {
        return this.userOrders.filter(order => order.payment_method === paymentType);
    }

    getCompletedOrders(): Order[] {
        return this.userOrders.filter(order => Number(order.pss_plan.payments_remaining) === 0);
    }

    getActiveOrders(): Order[] {
        return this.userOrders.filter(order =>
            !order.id &&
            (order.payment_status !== OrderStatus.CANCELLED) &&
            (order.payment_status !== OrderStatus.REFUNDED)
        );
    }

    private setLoading(loading: boolean): void {
        this.isLoading = loading;
    }

    private setCreating(creating: boolean): void {
        this.isCreating = creating;
    }

    private setError(error: string | null): void {
        this.error = error;
    }
}
